import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import HomeIcon from "@mui/icons-material/Home";
import React, { useEffect, useState } from "react";

import type { FolderIndex, FolderKey } from "./folderTree";

interface FolderPickerDialogProps {
  open: boolean;
  title: string;
  index: FolderIndex;
  /** Folders that cannot be chosen (a folder itself and everything beneath it). */
  disabledIds: ReadonlySet<number>;
  /** Where the item is now, so it can't be "moved" to the same place. */
  currentId: FolderKey;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (target: FolderKey) => void;
}

/** Pick a destination folder from an indented list of the whole tree. */
export default function FolderPickerDialog({
  open,
  title,
  index,
  disabledIds,
  currentId,
  busy,
  error,
  onClose,
  onSelect,
}: FolderPickerDialogProps) {
  const [selected, setSelected] = useState<FolderKey | undefined>(undefined);
  useEffect(() => {
    if (open) setSelected(undefined);
  }, [open]);

  const renderChildren = (parent: FolderKey, depth: number): React.ReactNode =>
    (index.childrenOf.get(parent) ?? []).map((folder) => {
      const disabled = disabledIds.has(folder.id);
      const isCurrent = currentId === folder.id;
      return (
        <React.Fragment key={folder.id}>
          <ListItemButton
            dense
            disabled={disabled}
            selected={selected === folder.id}
            onClick={() => setSelected(folder.id)}
            sx={{ pl: 2 + depth * 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <FolderIcon fontSize="small" sx={{ color: disabled ? "#d1d5db" : "#f59e0b" }} />
            </ListItemIcon>
            <ListItemText primary={folder.name} secondary={isCurrent ? "Current location" : undefined} />
          </ListItemButton>
          {renderChildren(folder.id, depth + 1)}
        </React.Fragment>
      );
    });

  const canConfirm = selected !== undefined && selected !== currentId && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
          <ListItemButton dense selected={selected === null} onClick={() => setSelected(null)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <HomeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="All projects (top level)"
              secondary={currentId === null ? "Current location" : undefined}
            />
          </ListItemButton>
          {renderChildren(null, 1)}
        </List>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!canConfirm}
          onClick={() => {
            if (selected !== undefined) onSelect(selected);
          }}
        >
          {busy ? "Moving…" : "Move here"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
